package top.wcpe.allincore.dsl.compiler.api

/**
 * 元素 kind 的 prop 元数据 SSOT（M8 阶段 C 引入；本轮做 UI 新增语法/特效的穷举对齐）。
 *
 * 与 [IdeMetadata] 互补：
 * - [IdeMetadata]：扁平的"元素 kind 白名单 + 单字段枚举值"集合，已被 ElementValidator 消费
 * - [PropMetadata]：每个 kind 的完整 prop 列表（含类型 / 是否必需 / 默认值 / 中文文档），
 *   IDE 端用于补全候选 / 属性面板 editor 分发 / hover 文档；编译器侧暂不消费（不接入
 *   ElementValidator 的"未知属性"诊断，避免现有项目突然多大量 WARNING）
 *
 * **三类 prop 列表**（[getProps] 按此顺序拼接并按 name 去重，前者优先）：
 * 1. [propsByKindCore]：各 kind 的**专有属性**（runtime 中 applier 用 `instanceof` 收窄到该元素类）
 * 2. [commonStyleProps]：**通用样式属性**（runtime 中对所有 `UIElement` 生效的尺寸/盒模型/Flex/
 *    背景边框/渐变/阴影/变换/字体等），仅供 IDE 补全 / hover，**不参与** ComponentExpanderPass
 *    的"组件根属性转发白名单"
 * 3. [universalProps]：**元素级控制属性**（visible / opacity / overflow 等），与
 *    [IdeMetadata.universalElementProps] 及 ComponentExpanderPass.UNIVERSAL_ELEMENT_PROPS
 *    保持「三处一致」——扩它会改组件展开语义，**不要动**
 *
 * **真源**：属性键以 `ui/runtime` 的 `ir/prop` 下各 PropContributor 为准（中央注册表 PropAppliers，
 * applier 内 `instanceof` 决定 kind 适用性）。本表登记需与之逐键核对，不得凭文档/推测。
 *
 * 设计目标：
 * - **IDE prop 名补全**：在 element block 体内 `<caret>` 时列出本 kind 的已知 prop
 * - **IDE prop 值补全**：在 `variant = <caret>` 时列出枚举值
 * - **IDE 属性面板**：根据 [PropDef.type] 选择对应 editor（如 EnumPropertyEditor）
 * - **IDE hover 文档**：渲染 prop 类型 / 默认值 / 中文说明
 */
object PropMetadata {

    /**
     * prop 的类型分类。
     *
     * 对应 IDE 端属性面板 editor 与补全候选的分发依据。第一版仅覆盖最常见的 6 类，
     * 后续 milestones 可补充 `Length` / `NodeId` / `Lambda` / `EventHandler` 等更细分类型。
     */
    enum class PropType {
        /** 数字字面量（含整数与浮点）。 */
        Number,

        /** 普通字符串字面量。 */
        String,

        /** 颜色字符串 `#RGB` / `#RRGGBB` / `#AARRGGBB`。 */
        Color,

        /** 布尔字面量 `true` / `false`。 */
        Boolean,

        /** 枚举字符串字面量（取值范围由 [PropDef.enumValues] 限定）。 */
        EnumValues,

        /** 表达式（fun / lambda / 列表 / Map / 表达式调用等），属性面板走 ExpressionEditor。 */
        Expression,
    }

    /**
     * 单个 prop 的元数据。
     *
     * @property name prop 名（与 PROPERTY_ASSIGN.PROPERTY_NAME 文本一致）
     * @property type prop 类型分类
     * @property enumValues 当 [type] 为 [PropType.EnumValues] 时的合法字面量集合；其它类型留空
     * @property required 是否为必传（第一版未消费，留待编译器侧诊断启用时使用）
     * @property doc 简短中文文档，用于 IDE hover 显示
     * @property defaultValue 默认值文本表示（hover 时显示，不强求与运行时默认严格一致）
     * @property animatable 是否可被动画块（transition / keyframe / frame / pathTransition）
     *     消费补间——IDE 在动画块内补 prop 名时仅列 `animatable=true` 的子集，避免把
     *     `pointerEvents` 这类逻辑型 prop 误推荐为可补间属性。默认 `false` 保持向后兼容。
     */
    data class PropDef(
        val name: String,
        val type: PropType,
        val enumValues: Set<String> = emptySet(),
        val required: Boolean = false,
        val doc: String,
        val defaultValue: String? = null,
        val animatable: Boolean = false,
    )

    /** 数量徽标 9 宫格锚点合法值集合（slot / itemstack 共用）。 */
    private val countAnchorValues: Set<String> = setOf(
        "top-left", "top", "top-right",
        "left", "center", "right",
        "bottom-left", "bottom", "bottom-right",
    )

    /**
     * 滚动容器（runtime `ScrollableContainer` 子类）共有属性。
     *
     * 适用 kind：scrollview / virtualList / virtualGrid / virtualTree / virtualMasonry /
     * multiColumnList / pageview / carousel。装饰器 kind（pullToRefresh / zoomable 等继承
     * `ScrollDecorator extends UIElement`）**不吃**这组。
     */
    private val scrollContainerProps: List<PropDef> = listOf(
        PropDef("direction", PropType.EnumValues, enumValues = IdeMetadata.scrollDirectionValues, doc = "滚动方向：vertical / horizontal / both", defaultValue = "\"vertical\""),
        PropDef("scrollbarMode", PropType.EnumValues, enumValues = IdeMetadata.scrollbarModeValues, doc = "滚动条显示模式：auto / always / never", defaultValue = "\"auto\""),
        PropDef("scrollbarColor", PropType.Color, doc = "滚动条滑块颜色"),
        PropDef("scrollbarTrackColor", PropType.Color, doc = "滚动条轨道颜色"),
        PropDef("scrollbarWidth", PropType.Number, doc = "滚动条宽度（像素）"),
        PropDef("mouseWheelStep", PropType.Number, doc = "滚轮每格滚动距离（像素），默认 20"),
        PropDef("bounce", PropType.Boolean, doc = "是否启用边缘回弹", defaultValue = "false"),
        PropDef("bounceMaxDistance", PropType.Number, doc = "回弹最大距离（像素）"),
        PropDef("nestedScroll", PropType.Boolean, doc = "是否启用嵌套滚动链", defaultValue = "false"),
        PropDef("inertia", PropType.Boolean, doc = "是否启用惯性滚动", defaultValue = "false"),
        PropDef("inertiaDecay", PropType.Number, doc = "惯性衰减系数"),
        PropDef("smoothScroll", PropType.Boolean, doc = "是否启用平滑滚动", defaultValue = "false"),
        PropDef("smoothScrollDurationMs", PropType.Number, doc = "平滑滚动时长（毫秒）"),
    )

    /**
     * 各元素 kind 的**专有** prop 列表表。
     *
     * 只列该 kind 经 runtime `instanceof` 收窄真正消费的属性；通用样式属性见 [commonStyleProps]，
     * 元素级控制属性见 [universalProps]，均由 [getProps] 自动追加，不在此重复。
     */
    private val propsByKindCore: Map<String, List<PropDef>> = mapOf(
        // ===== 基础容器（无专有属性，仅吃通用样式 / 控制属性）=====
        "box" to emptyList(),
        "row" to emptyList(),
        "column" to emptyList(),
        "stack" to emptyList(),
        "scrollview" to scrollContainerProps,
        "grid" to listOf(
            PropDef("columns", PropType.Number, doc = "等宽列数（等价 repeat(N, 1fr)）"),
            PropDef("rows", PropType.Number, doc = "行数"),
            PropDef(
                "gridTemplateColumns", PropType.String,
                doc = "列 track-list（CSS grid-template-columns 文法，如 \"80px 1fr 2fr\" / \"repeat(3, 1fr)\" / \"minmax(100px, 1fr) 2fr\"）；写入时切到 grid 布局",
            ),
            PropDef(
                "gridTemplateRows", PropType.String,
                doc = "行 track-list（CSS grid-template-rows 文法）；写入时切到 grid 布局",
            ),
            PropDef(
                name = "gridAutoFlow",
                type = PropType.EnumValues,
                enumValues = IdeMetadata.gridAutoFlowValues,
                doc = "隐式轨道自动流向：row / column / row-dense / column-dense",
                defaultValue = "\"row\"",
            ),
            PropDef(
                name = "justifyItems",
                type = PropType.EnumValues,
                enumValues = IdeMetadata.gridJustifyItemsValues,
                doc = "grid 子项主轴对齐：start / center / end / stretch",
            ),
        ),

        // ===== 虚拟滚动容器 =====
        "virtualList" to scrollContainerProps + listOf(
            PropDef("itemHeight", PropType.Number, doc = "单项高度（像素）", required = true, animatable = true),
            PropDef("itemCount", PropType.Number, doc = "数据项总数"),
            PropDef("source", PropType.Expression, doc = "数据源表达式（List / Map）"),
            PropDef("buffer", PropType.Number, doc = "视口外预渲染缓冲项数"),
        ),
        "virtualGrid" to scrollContainerProps + listOf(
            PropDef("columns", PropType.Number, doc = "列数"),
            PropDef("rows", PropType.Number, doc = "行数"),
            PropDef("itemWidth", PropType.Number, doc = "单项宽度（像素）"),
            PropDef("itemHeight", PropType.Number, doc = "单项高度（像素）"),
            PropDef("itemCount", PropType.Number, doc = "数据项总数"),
            PropDef("gapX", PropType.Number, doc = "横向间距"),
            PropDef("gapY", PropType.Number, doc = "纵向间距"),
            PropDef("buffer", PropType.Number, doc = "预渲染缓冲项数"),
            PropDef("flow", PropType.EnumValues, enumValues = IdeMetadata.virtualGridFlowValues, doc = "排布流向：row-major / column-major"),
        ),
        "virtualMasonry" to scrollContainerProps + listOf(
            PropDef("columns", PropType.Number, doc = "瀑布流列数"),
            PropDef("itemWidth", PropType.Number, doc = "单项宽度（像素）"),
            PropDef("gapX", PropType.Number, doc = "横向间距"),
            PropDef("gapY", PropType.Number, doc = "纵向间距"),
            PropDef("buffer", PropType.Number, doc = "预渲染缓冲距离"),
            PropDef("itemHeights", PropType.Expression, doc = "各项高度列表（List<Number>）"),
        ),
        "virtualTree" to scrollContainerProps + listOf(
            PropDef("itemHeight", PropType.Number, doc = "单项高度（像素）"),
            PropDef("buffer", PropType.Number, doc = "预渲染缓冲项数"),
            PropDef("indentPerDepth", PropType.Number, doc = "每层缩进（像素）"),
        ),
        "multiColumnList" to scrollContainerProps + listOf(
            PropDef("itemHeight", PropType.Number, doc = "单项高度（像素）"),
            PropDef("itemCount", PropType.Number, doc = "数据项总数"),
            PropDef("buffer", PropType.Number, doc = "预渲染缓冲项数"),
            PropDef("headerHeight", PropType.Number, doc = "表头高度（像素）"),
            PropDef("headerSticky", PropType.Boolean, doc = "表头是否吸顶", defaultValue = "false"),
            PropDef("columnDefs", PropType.Expression, doc = "列定义列表（List<Map{key,title,width,align}>）"),
        ),
        "pageview" to scrollContainerProps + listOf(
            PropDef("currentPage", PropType.Number, doc = "当前页索引"),
            PropDef("showIndicator", PropType.Boolean, doc = "是否显示页指示器", defaultValue = "false"),
            PropDef("indicatorPosition", PropType.EnumValues, enumValues = IdeMetadata.indicatorPositionValues, doc = "指示器位置：bottom / top / left / right"),
        ),
        "carousel" to scrollContainerProps + listOf(
            PropDef("currentPage", PropType.Number, doc = "当前页索引"),
            PropDef("showIndicator", PropType.Boolean, doc = "是否显示页指示器", defaultValue = "false"),
            PropDef("indicatorPosition", PropType.EnumValues, enumValues = IdeMetadata.indicatorPositionValues, doc = "指示器位置：bottom / top / left / right"),
            PropDef("loop", PropType.Boolean, doc = "是否循环轮播", defaultValue = "false"),
            PropDef("autoPlay", PropType.Boolean, doc = "是否自动播放", defaultValue = "false"),
            PropDef("autoPlayIntervalMs", PropType.Number, doc = "自动播放间隔（毫秒）"),
        ),

        // ===== 滚动装饰器（ScrollDecorator，不吃 scrollContainerProps）=====
        "pullToRefresh" to listOf(
            PropDef("pullIndicatorHeight", PropType.Number, doc = "下拉指示器高度（像素）"),
            PropDef("pullText", PropType.String, doc = "下拉中提示文本"),
            PropDef("releaseText", PropType.String, doc = "松手刷新提示文本"),
            PropDef("refreshingText", PropType.String, doc = "刷新中提示文本"),
            PropDef("threshold", PropType.Number, doc = "触发刷新的下拉阈值（像素）"),
        ),
        "infiniteScroll" to listOf(
            PropDef("hasMore", PropType.Boolean, doc = "是否还有更多数据", defaultValue = "true"),
            PropDef("loadingText", PropType.String, doc = "加载中提示文本"),
            PropDef("noMoreText", PropType.String, doc = "无更多数据提示文本"),
            PropDef("threshold", PropType.Number, doc = "触发加载的距底阈值（像素）"),
        ),
        "collapsingHeader" to listOf(
            PropDef("expandedHeight", PropType.Number, doc = "展开态高度（像素）"),
            PropDef("collapsedHeight", PropType.Number, doc = "折叠态高度（像素）"),
            PropDef("pinned", PropType.Boolean, doc = "折叠后是否固定保留", defaultValue = "false"),
        ),
        "zoomable" to listOf(
            PropDef("zoom", PropType.Number, doc = "当前缩放倍率", animatable = true),
            PropDef("minZoom", PropType.Number, doc = "最小缩放倍率"),
            PropDef("maxZoom", PropType.Number, doc = "最大缩放倍率"),
            PropDef("zoomStep", PropType.Number, doc = "每步缩放增量"),
            PropDef("zoomFocus", PropType.EnumValues, enumValues = IdeMetadata.zoomFocusValues, doc = "缩放焦点：mouse / center"),
        ),
        "autoScroll" to listOf(
            PropDef("speed", PropType.Number, doc = "自动滚动速度（像素/秒）"),
            PropDef("paused", PropType.Boolean, doc = "是否暂停", defaultValue = "false"),
            PropDef("pauseOnHover", PropType.Boolean, doc = "悬停时是否暂停", defaultValue = "false"),
            PropDef("loopBack", PropType.Boolean, doc = "到底后是否回卷", defaultValue = "false"),
        ),
        "dragReorder" to listOf(
            PropDef("longPressMs", PropType.Number, doc = "长按触发拖拽的毫秒数"),
            PropDef("dragHandleId", PropType.String, doc = "拖拽手柄子元素 id"),
        ),
        "splitter" to listOf(
            PropDef("ratio", PropType.Number, doc = "分隔比例（0..1）", animatable = true),
            PropDef("minRatio", PropType.Number, doc = "最小分隔比例"),
            PropDef("maxRatio", PropType.Number, doc = "最大分隔比例"),
            PropDef("orientation", PropType.String, doc = "分隔方向（horizontal / vertical）"),
            PropDef("total", PropType.Number, doc = "总尺寸（像素）"),
        ),

        // ===== 文本与图标 =====
        "text" to listOf(
            PropDef("text", PropType.String, doc = "显示文本"),
            PropDef("size", PropType.Number, doc = "字号倍率（基线 1.0）", defaultValue = "1.0", animatable = true),
            PropDef("color", PropType.Color, doc = "文字颜色", animatable = true),
            PropDef(
                name = "typography",
                type = PropType.EnumValues,
                enumValues = IdeMetadata.textTypographyRoles,
                doc = "Material Design 3 typography 角色",
            ),
            PropDef("multiline", PropType.Boolean, doc = "是否允许多行换行", defaultValue = "false"),
            PropDef("richText", PropType.Boolean, doc = "是否按富文本解析（[icon:xx] 标签/图文混排/§颜色码）", defaultValue = "false"),
            PropDef("textAlign", PropType.EnumValues, enumValues = IdeMetadata.textAlignValues, doc = "文本水平对齐：left / center / right"),
            PropDef("maxLines", PropType.Number, doc = "最大行数（超出按 textOverflow 处理）"),
            PropDef("textOverflow", PropType.String, doc = "溢出处理（wrap / ellipsis 等），默认 wrap"),
            // v0.12 新增：文本排版原语（runtime VisualPropContributor 收窄到 Text）
            PropDef("letterSpacing", PropType.Number, doc = "字距（像素）", animatable = true),
            PropDef("lineHeight", PropType.Number, doc = "行高（像素）", animatable = true),
            PropDef("textDecoration", PropType.String, doc = "文本装饰：none / underline / line-through"),
            PropDef("decipher", PropType.Boolean, doc = "是否启用逐字乱码揭示动画", defaultValue = "false"),
            PropDef("decipherDurationMs", PropType.Number, doc = "乱码揭示动画时长（毫秒）"),
            PropDef("decipherCharset", PropType.String, doc = "乱码揭示使用的字符集"),
        ),
        "image" to listOf(
            // 修正：runtime 真实键是 resource（旧 SSOT 的 src 不被消费）
            PropDef("resource", PropType.String, doc = "图片资源标识（namespaced 路径或 vanilla 容器贴图）", required = true),
            PropDef("uvX", PropType.Number, doc = "贴图采样 U 起点"),
            PropDef("uvY", PropType.Number, doc = "贴图采样 V 起点"),
            PropDef("uvW", PropType.Number, doc = "贴图采样宽度"),
            PropDef("uvH", PropType.Number, doc = "贴图采样高度"),
            PropDef("uvRotation", PropType.Number, doc = "贴图旋转角度"),
            PropDef("uvScaleX", PropType.Number, doc = "贴图横向缩放"),
            PropDef("uvScaleY", PropType.Number, doc = "贴图纵向缩放"),
            PropDef("uvOffsetX", PropType.Number, doc = "贴图横向偏移"),
            PropDef("uvOffsetY", PropType.Number, doc = "贴图纵向偏移"),
            PropDef("imageMode", PropType.String, doc = "贴图模式（NORMAL / REPEAT / MIRROR）", defaultValue = "NORMAL"),
            PropDef("tintColor", PropType.Color, doc = "贴图染色叠加色", animatable = true),
        ),
        "divider" to listOf(
            PropDef("thickness", PropType.Number, doc = "厚度（像素）", defaultValue = "1", animatable = true),
            PropDef("color", PropType.Color, doc = "分隔线颜色", animatable = true),
        ),
        "spacer" to listOf(
            PropDef("size", PropType.Number, doc = "间距大小（像素）", animatable = true),
        ),

        // ===== 交互控件 =====
        "button" to listOf(
            PropDef("text", PropType.String, doc = "按钮文本"),
            PropDef(
                name = "variant",
                type = PropType.EnumValues,
                enumValues = IdeMetadata.buttonVariants,
                doc = "Material Design 3 按钮风格",
                defaultValue = "\"filled\"",
            ),
            PropDef("onClick", PropType.Expression, doc = "点击回调（fun 引用或 lambda）"),
            PropDef("enabled", PropType.Boolean, doc = "是否启用", defaultValue = "true"),
            PropDef("color", PropType.Color, doc = "文字颜色", animatable = true),
            PropDef("size", PropType.Number, doc = "文字字号倍率", defaultValue = "1.0", animatable = true),
            PropDef("icon", PropType.String, doc = "图标资源标识"),
            PropDef("iconSize", PropType.Number, doc = "图标尺寸（像素）"),
            PropDef("iconPosition", PropType.String, doc = "图标位置（left / right / top / bottom）"),
            PropDef("elevation", PropType.Number, doc = "投影高度（elevated 风格）", animatable = true),
            PropDef("loading", PropType.Boolean, doc = "是否显示加载态", defaultValue = "false"),
            PropDef("loadingText", PropType.String, doc = "加载态替换文本"),
            PropDef("textTransform", PropType.String, doc = "文本大小写变换（none / upper / lower）"),
            PropDef("textAlign", PropType.EnumValues, enumValues = IdeMetadata.textAlignValues, doc = "文本水平对齐：left / center / right"),
            PropDef("multiline", PropType.Boolean, doc = "是否允许多行", defaultValue = "false"),
            PropDef("hoverOverlayColor", PropType.Color, doc = "悬停叠加色"),
            PropDef("hoverOverlayOpacity", PropType.Number, doc = "悬停叠加透明度"),
            PropDef("pressedOverlayColor", PropType.Color, doc = "按下叠加色"),
            PropDef("pressedOverlayOpacity", PropType.Number, doc = "按下叠加透明度"),
            PropDef("focusOverlayColor", PropType.Color, doc = "聚焦叠加色"),
            PropDef("focusBorderColor", PropType.Color, doc = "聚焦边框色"),
            PropDef("disabledOpacity", PropType.Number, doc = "禁用态透明度"),
            PropDef("glowIntensityScale", PropType.Number, doc = "发光强度缩放", animatable = true),
            PropDef("glowSizeBoost", PropType.Number, doc = "发光尺寸增量", animatable = true),
        ),
        "checkbox" to listOf(
            PropDef("checked", PropType.Boolean, doc = "选中状态", required = true),
            PropDef("onChange", PropType.Expression, doc = "状态变更回调"),
            PropDef("label", PropType.String, doc = "右侧标签文本"),
            PropDef("checkColor", PropType.Color, doc = "勾选标记颜色"),
            PropDef("boxBackgroundColor", PropType.Color, doc = "勾选框背景色"),
            PropDef("boxBorderColor", PropType.Color, doc = "勾选框边框色"),
            PropDef("focusBorderColor", PropType.Color, doc = "聚焦边框色"),
            PropDef("checkInset", PropType.Number, doc = "勾选标记内缩（像素）"),
            PropDef("disabledOpacity", PropType.Number, doc = "禁用态透明度"),
        ),
        "switch" to listOf(
            PropDef("checked", PropType.Boolean, doc = "开关状态", required = true),
            PropDef("onChange", PropType.Expression, doc = "状态变更回调"),
            PropDef("enabled", PropType.Boolean, doc = "是否启用", defaultValue = "true"),
            PropDef("trackColorOn", PropType.Color, doc = "开启态轨道色"),
            PropDef("trackColorOff", PropType.Color, doc = "关闭态轨道色"),
            PropDef("thumbColorOn", PropType.Color, doc = "开启态滑块色"),
            PropDef("thumbColorOff", PropType.Color, doc = "关闭态滑块色"),
            PropDef("thumbInset", PropType.Number, doc = "滑块内缩（像素）"),
            PropDef("trackCornerRadius", PropType.Number, doc = "轨道圆角（像素）"),
            PropDef("thumbCornerRadius", PropType.Number, doc = "滑块圆角（像素）"),
        ),
        "radio" to listOf(
            PropDef("value", PropType.String, doc = "本单选项的值", required = true),
            PropDef("label", PropType.String, doc = "右侧标签"),
            PropDef("selected", PropType.Boolean, doc = "是否选中"),
            PropDef("groupValue", PropType.String, doc = "所属组当前选中值"),
            PropDef("dotColor", PropType.Color, doc = "选中圆点颜色"),
            PropDef("boxBackgroundColor", PropType.Color, doc = "单选框背景色"),
            PropDef("boxBorderColor", PropType.Color, doc = "单选框边框色"),
        ),
        "radiogroup" to listOf(
            PropDef("value", PropType.String, doc = "当前选中的子 radio.value"),
            PropDef("selectedValue", PropType.String, doc = "当前选中值（runtime 真实键）"),
            PropDef("onChange", PropType.Expression, doc = "选中变更回调"),
        ),
        "slider" to listOf(
            PropDef("value", PropType.Number, doc = "当前值", required = true),
            PropDef("min", PropType.Number, doc = "最小值", defaultValue = "0"),
            PropDef("max", PropType.Number, doc = "最大值", defaultValue = "1"),
            PropDef("step", PropType.Number, doc = "步长", defaultValue = "0"),
            PropDef("onChange", PropType.Expression, doc = "值变更回调"),
            PropDef("trackColor", PropType.Color, doc = "轨道颜色"),
            PropDef("fillColor", PropType.Color, doc = "已填充部分颜色"),
            PropDef("thumbColor", PropType.Color, doc = "滑块颜色"),
            PropDef("arrowColor", PropType.Color, doc = "箭头颜色"),
            PropDef("trackHeight", PropType.Number, doc = "轨道高度（像素）"),
            PropDef("thumbSize", PropType.Number, doc = "滑块尺寸（像素）"),
            PropDef("trackCornerRadius", PropType.Number, doc = "轨道圆角（像素）"),
        ),
        "textfield" to listOf(
            PropDef("value", PropType.String, doc = "当前文本", required = true),
            PropDef("placeholder", PropType.String, doc = "占位提示"),
            PropDef("onChange", PropType.Expression, doc = "文本变更回调"),
            PropDef("caretWidth", PropType.Number, doc = "光标宽度(px)", defaultValue = "2"),
            PropDef("caretColor", PropType.Color, doc = "光标颜色(ARGB hex)，默认跟随主题"),
            PropDef("caretBlink", PropType.Boolean, doc = "光标是否闪烁", defaultValue = "true"),
            PropDef("caretBlinkRate", PropType.Number, doc = "光标闪烁频率(Hz)", defaultValue = "1.2"),
            PropDef("password", PropType.Boolean, doc = "密码模式，开启后用掩码字符遮蔽输入内容（仅遮蔽显示，onChange 回调仍可读到明文 value）", defaultValue = "false"),
            PropDef("passwordChar", PropType.String, doc = "密码模式掩码字符，默认 *", defaultValue = "*"),
            PropDef("maxLength", PropType.Number, doc = "最大输入字符数，0 表示不限制", defaultValue = "0"),
            PropDef("readOnly", PropType.Boolean, doc = "只读模式，可聚焦但不可编辑", defaultValue = "false"),
            PropDef("enabled", PropType.Boolean, doc = "是否启用，禁用后不可聚焦并置灰", defaultValue = "true"),
            PropDef("textColor", PropType.Color, doc = "文本颜色(ARGB hex)，默认跟随主题"),
            PropDef("placeholderColor", PropType.Color, doc = "占位提示颜色"),
            PropDef("focusBorderColor", PropType.Color, doc = "聚焦边框色"),
            PropDef("textPaddingX", PropType.Number, doc = "文本左右内边距（像素）"),
            PropDef(
                name = "textAlign",
                type = PropType.EnumValues,
                enumValues = IdeMetadata.textAlignValues,
                doc = "文本水平对齐",
                defaultValue = "left",
            ),
            PropDef("onSubmit", PropType.Expression, doc = "回车提交回调"),
            PropDef("multiline", PropType.Boolean, doc = "多行文本域：开启后裸 Enter 换行、放行换行符、按视觉行渲染并支持 Up/Down 跨行", defaultValue = "false"),
            PropDef("selectionColor", PropType.Color, doc = "选区高亮色(ARGB hex)，默认跟随主题 primary 叠半透明"),
            PropDef("submitOnEnter", PropType.Boolean, doc = "回车提交策略覆盖：true=裸 Enter 始终提交；false=Enter 永不提交（多行下仅换行）。默认单行 Enter 提交 / 多行 Ctrl+Enter 提交"),
        ),
        "dropdown" to listOf(
            PropDef("value", PropType.String, doc = "当前选中的子 option.value"),
            PropDef("selectedValue", PropType.String, doc = "当前选中值（runtime 真实键）"),
            PropDef("items", PropType.Expression, doc = "动态选项列表（List<String> 或 List<Map>，Map 包含 value/label）"),
            PropDef("onChange", PropType.Expression, doc = "选项变更回调"),
            PropDef("size", PropType.Number, doc = "字号倍率（基线 1.0）", defaultValue = "1.0", animatable = true),
            PropDef("textColor", PropType.Color, doc = "文本颜色"),
            PropDef("arrowColor", PropType.Color, doc = "下拉箭头颜色"),
            PropDef("panelColor", PropType.Color, doc = "下拉面板背景色"),
            PropDef("optionHoverColor", PropType.Color, doc = "选项悬停色"),
            PropDef("selectedOptionColor", PropType.Color, doc = "选中项背景色"),
            PropDef("headerColor", PropType.Color, doc = "头部背景色"),
            PropDef("optionHeight", PropType.Number, doc = "单项高度（像素）"),
            PropDef("panelCornerRadius", PropType.Number, doc = "面板圆角（像素）"),
        ),
        "option" to listOf(
            PropDef("value", PropType.String, doc = "选项值", required = true),
            PropDef("label", PropType.String, doc = "显示文本（默认回退到 value）"),
        ),
        "tabview" to listOf(
            // 修正：runtime 真实键是 activeIndex（Number），旧 SSOT 的 activeTab(String) 不被消费
            PropDef("activeIndex", PropType.Number, doc = "当前激活 tab 的索引"),
            PropDef("onChange", PropType.Expression, doc = "tab 切换回调"),
            PropDef("tabActiveBackgroundColor", PropType.Color, doc = "激活 tab 背景色"),
            PropDef("tabInactiveBackgroundColor", PropType.Color, doc = "非激活 tab 背景色"),
            PropDef("tabActiveTextColor", PropType.Color, doc = "激活 tab 文字色"),
            PropDef("tabInactiveTextColor", PropType.Color, doc = "非激活 tab 文字色"),
            PropDef("tabIndicatorColor", PropType.Color, doc = "tab 指示条颜色"),
            PropDef("headerHeight", PropType.Number, doc = "tab 头部高度（像素）"),
            PropDef("tabPadding", PropType.Number, doc = "tab 内边距（像素）"),
        ),
        "tab" to listOf(
            PropDef("title", PropType.String, doc = "tab 标题", required = true),
        ),

        // ===== 进度 / 弹层 =====
        "progressbar" to listOf(
            PropDef("value", PropType.Number, doc = "当前进度（0..1）", required = true, animatable = true),
            PropDef("max", PropType.Number, doc = "最大值", defaultValue = "1"),
            PropDef("color", PropType.Color, doc = "进度条填充颜色", animatable = true),
            PropDef("barColor", PropType.Color, doc = "进度条填充颜色（别名）", animatable = true),
            PropDef("notches", PropType.Number, doc = "刻度分段数"),
            PropDef("notchColor", PropType.Color, doc = "刻度颜色"),
            PropDef("animateValue", PropType.Boolean, doc = "value 变化是否平滑过渡", defaultValue = "true"),
            PropDef("valueTransitionMs", PropType.Number, doc = "过渡时长（毫秒）", defaultValue = "200"),
            PropDef("barCornerRadius", PropType.Number, doc = "填充前景圆角（像素）"),
        ),
        "modal" to listOf(
            PropDef("visible", PropType.Boolean, doc = "是否可见", required = true),
            PropDef("onDismiss", PropType.Expression, doc = "关闭回调"),
            PropDef("dismissOnOutsideClick", PropType.Boolean, doc = "点击遮罩外是否关闭", defaultValue = "true"),
            PropDef("dismissOnEsc", PropType.Boolean, doc = "按 ESC 是否关闭", defaultValue = "true"),
            PropDef("centered", PropType.Boolean, doc = "是否居中", defaultValue = "true"),
            PropDef("fullscreen", PropType.Boolean, doc = "是否全屏", defaultValue = "false"),
            PropDef("backdrop", PropType.Color, doc = "遮罩背景色"),
            PropDef("fadeIn", PropType.Number, doc = "淡入时长（毫秒）"),
            PropDef("offsetX", PropType.Number, doc = "横向偏移（像素）"),
            PropDef("offsetY", PropType.Number, doc = "纵向偏移（像素）"),
            PropDef("anchorTarget", PropType.String, doc = "锚定目标元素 id"),
            PropDef("anchorPosition", PropType.String, doc = "锚定相对位置"),
            PropDef("anchorAlign", PropType.String, doc = "锚定对齐方式"),
            PropDef("spotlightTarget", PropType.String, doc = "聚光灯高亮目标元素 id"),
        ),
        "toast" to listOf(
            PropDef("visible", PropType.Boolean, doc = "是否可见", required = true),
            PropDef("message", PropType.String, doc = "提示文本"),
            PropDef(
                name = "kind",
                type = PropType.EnumValues,
                enumValues = IdeMetadata.toastKinds,
                doc = "提示等级：info / success / warning / error",
                defaultValue = "\"info\"",
            ),
            PropDef(
                name = "position",
                type = PropType.EnumValues,
                enumValues = IdeMetadata.toastPositions,
                doc = "弹出位置",
                defaultValue = "\"top\"",
            ),
            PropDef("duration", PropType.Number, doc = "自动消失毫秒数（0 则不自动消失）"),
            PropDef("backgroundColor", PropType.Color, doc = "背景色"),
            PropDef("textColor", PropType.Color, doc = "文字色"),
        ),

        // ===== 物品 / 槽位 =====
        "itemstack" to listOf(
            PropDef("itemId", PropType.String, doc = "物品 ID（如 minecraft:stone）", required = true),
            PropDef("count", PropType.Number, doc = "堆叠数量", defaultValue = "1"),
            PropDef("durability", PropType.Number, doc = "耐久（剩余值）"),
            PropDef("enchanted", PropType.Boolean, doc = "是否带附魔光效", defaultValue = "false"),
            PropDef("cooldown", PropType.Number, doc = "冷却进度（0..1 灰罩）"),
            PropDef("displayName", PropType.String, doc = "覆盖默认物品名"),
            PropDef("lore", PropType.Expression, doc = "物品描述行列表（List<String>）"),
            PropDef("customNbt", PropType.Expression, doc = "自定义 NBT（Map）"),
            PropDef("showBackground", PropType.Boolean, doc = "是否显示槽位背景", defaultValue = "false"),
            PropDef("backgroundColor", PropType.Color, doc = "槽位背景色"),
            PropDef("backgroundBorderWidth", PropType.Number, doc = "背景边框宽度（像素）"),
            PropDef(
                name = "countAnchor",
                type = PropType.EnumValues,
                enumValues = countAnchorValues,
                doc = "数量徽标的 9 宫格锚点；默认 bottom-right 复刻 vanilla 右下角",
                defaultValue = "\"bottom-right\"",
            ),
            PropDef("countOffsetX", PropType.Number, doc = "数量徽标 X 偏移（像素）", defaultValue = "0"),
            PropDef("countOffsetY", PropType.Number, doc = "数量徽标 Y 偏移（像素）", defaultValue = "0"),
        ),
        "slot" to listOf(
            PropDef("source", PropType.String, doc = "源槽位定义，如 player.hotbar", required = true),
            PropDef("index", PropType.Number, doc = "源槽位索引", required = true),
            PropDef("key", PropType.String, doc = "源槽位 key（与 index 二选一）"),
            PropDef("readId", PropType.String, doc = "只读镜像源 id（slot.read 转传）"),
            PropDef("item", PropType.Expression, doc = "直接绑定的 ItemView / Map"),
            PropDef("slotSize", PropType.Number, doc = "槽位边长（像素）", defaultValue = "18", animatable = true),
            PropDef("borderColor", PropType.Color, doc = "槽位边框色"),
            PropDef("borderWidth", PropType.Number, doc = "槽位边框宽度（像素）"),
            PropDef("selected", PropType.Boolean, doc = "是否选中高亮"),
            PropDef("selectedOutlineColor", PropType.Color, doc = "选中描边色"),
            PropDef("showBackground", PropType.Boolean, doc = "是否显示槽位背景"),
            PropDef("backgroundColor", PropType.Color, doc = "槽位背景色"),
            PropDef("showTooltip", PropType.Boolean, doc = "是否显示物品 tooltip", defaultValue = "true"),
            PropDef("showCount", PropType.Boolean, doc = "是否显示数量徽标", defaultValue = "true"),
            PropDef("readOnly", PropType.Boolean, doc = "只读：禁止拖入拖出", defaultValue = "false"),
            PropDef("allowShiftTransfer", PropType.Boolean, doc = "是否允许 Shift 快速转移"),
            PropDef("allowDrop", PropType.Boolean, doc = "是否允许放入"),
            PropDef("allowPick", PropType.Boolean, doc = "是否允许取出"),
            PropDef("placeholder", PropType.String, doc = "空槽占位贴图"),
            PropDef(
                name = "countAnchor",
                type = PropType.EnumValues,
                enumValues = countAnchorValues,
                doc = "数量徽标的 9 宫格锚点；默认 bottom-right 复刻 vanilla 右下角",
                defaultValue = "\"bottom-right\"",
            ),
            PropDef("countOffsetX", PropType.Number, doc = "数量徽标 X 偏移（像素）", defaultValue = "0"),
            PropDef("countOffsetY", PropType.Number, doc = "数量徽标 Y 偏移（像素）", defaultValue = "0"),
        ),
        "slotGrid" to listOf(
            // 修正：动态展开真实键为 cols / cellSize / cellPadding / indexStart（IrInterpreter.buildDynamicSlotGrid）
            PropDef("source", PropType.String, doc = "源槽位定义", required = true),
            PropDef("cols", PropType.Number, doc = "列数", required = true),
            PropDef("cellSize", PropType.Number, doc = "单格边长（像素）", defaultValue = "18"),
            PropDef("cellPadding", PropType.Number, doc = "格间距（像素）", defaultValue = "1"),
            PropDef("indexStart", PropType.Number, doc = "起始槽位索引", defaultValue = "0"),
        ),
        "inventory" to listOf(
            PropDef("source", PropType.String, doc = "源 inventory 定义", required = true),
        ),
        "entityPreview" to listOf(
            // 修正：runtime 真实键是 source（旧 SSOT 的 entityId 不被消费）
            PropDef("source", PropType.String, doc = "实体来源（如 self / 实体类型）", required = true),
            PropDef("entityType", PropType.String, doc = "实体类型 ID"),
            PropDef("entityName", PropType.String, doc = "实体显示名"),
            PropDef("scale", PropType.Number, doc = "3D 实体缩放", animatable = true),
            PropDef("rotationX", PropType.Number, doc = "实体绕 X 轴旋转角度", animatable = true),
            PropDef("rotationY", PropType.Number, doc = "实体绕 Y 轴旋转角度", animatable = true),
            PropDef("followMouse", PropType.Boolean, doc = "头部是否跟随鼠标", defaultValue = "false"),
            PropDef("backgroundColor", PropType.Color, doc = "预览背景色"),
        ),
        "modelPreview" to listOf(
            PropDef("source", PropType.String, doc = "相对声明文件解析的 .bbmodel 模型路径", required = true),
            PropDef("texture", PropType.String, doc = "可选的相对 PNG / JPEG / WebP 纹理覆盖", defaultValue = "null"),
            PropDef("scale", PropType.Number, doc = "模型缩放倍率，必须有限且大于零", defaultValue = "1.0", animatable = true),
            PropDef("rotationX", PropType.Number, doc = "模型绕 X 轴旋转角度", defaultValue = "0", animatable = true),
            PropDef("rotationY", PropType.Number, doc = "模型绕 Y 轴旋转角度", defaultValue = "0", animatable = true),
            PropDef("interactive", PropType.Boolean, doc = "是否允许指针拖拽旋转模型", defaultValue = "true"),
            PropDef("backgroundColor", PropType.Color, doc = "控件背景色与失败占位底色", defaultValue = "\"#00000000\""),
        ),

        // ===== 自绘 =====
        "canvas" to listOf(
            PropDef("width", PropType.Number, doc = "画布宽度（像素）", required = true, animatable = true),
            PropDef("height", PropType.Number, doc = "画布高度（像素）", required = true, animatable = true),
            PropDef("duration", PropType.Number, doc = "动画时长（毫秒），不设则只走 once 块"),
        ),

        // ===== 菜单专用 =====
        "item" to listOf(
            PropDef("text", PropType.String, doc = "菜单项显示文本", required = true),
            PropDef("onClick", PropType.Expression, doc = "点击回调"),
            PropDef("enabled", PropType.Boolean, doc = "是否启用", defaultValue = "true"),
        ),
    )

    /**
     * 通用样式属性（runtime 中对所有 `UIElement` 生效的尺寸/盒模型/Flex/背景边框/渐变/阴影/
     * 变换/字体等），由 [getProps] 自动追加给所有 kind，仅服务 IDE 补全 / hover / 属性面板。
     *
     * **与 [universalProps] 的区别**：本组**不参与** ComponentExpanderPass 的"组件根属性转发
     * 白名单"——它们登记在此只为 IDE 智能，不改变组件展开语义；[universalProps] 那 9 个才参与转发。
     *
     * 真源：`LayoutPropContributor` / `VisualPropContributor` / `TransformPropContributor`
     * 中**不做 instanceof 收窄**、直接写 `el.style()...` / 基类字段的键。
     */
    private val commonStyleProps: List<PropDef> = listOf(
        // ----- 尺寸 -----
        PropDef("width", PropType.Number, doc = "宽度（像素，或字符串 \"fill\" / \"100%\" / \"auto\"）", animatable = true),
        PropDef("height", PropType.Number, doc = "高度（像素，或字符串 \"fill\" / \"100%\" / \"auto\"）", animatable = true),
        PropDef("minWidth", PropType.Number, doc = "最小宽度", animatable = true),
        PropDef("minHeight", PropType.Number, doc = "最小高度", animatable = true),
        PropDef("maxWidth", PropType.Number, doc = "最大宽度", animatable = true),
        PropDef("maxHeight", PropType.Number, doc = "最大高度", animatable = true),
        // ----- 盒模型 -----
        PropDef("padding", PropType.Number, doc = "四向内边距（像素）", animatable = true),
        PropDef("paddingTop", PropType.Number, doc = "上内边距", animatable = true),
        PropDef("paddingRight", PropType.Number, doc = "右内边距", animatable = true),
        PropDef("paddingBottom", PropType.Number, doc = "下内边距", animatable = true),
        PropDef("paddingLeft", PropType.Number, doc = "左内边距", animatable = true),
        PropDef("margin", PropType.Number, doc = "四向外边距（像素）", animatable = true),
        PropDef("marginTop", PropType.Number, doc = "上外边距", animatable = true),
        PropDef("marginRight", PropType.Number, doc = "右外边距", animatable = true),
        PropDef("marginBottom", PropType.Number, doc = "下外边距", animatable = true),
        PropDef("marginLeft", PropType.Number, doc = "左外边距", animatable = true),
        PropDef("gap", PropType.Number, doc = "子元素间距（Row/Column 主轴）", animatable = true),
        // ----- Flex -----
        PropDef("flexGrow", PropType.Number, doc = "主轴扩张因子"),
        PropDef("flexShrink", PropType.Number, doc = "主轴收缩因子"),
        PropDef("flexBasis", PropType.Number, doc = "flex 基准尺寸"),
        PropDef("align", PropType.EnumValues, enumValues = IdeMetadata.alignValues, doc = "交叉轴对齐：start / center / end / stretch", defaultValue = "\"start\""),
        PropDef("justify", PropType.EnumValues, enumValues = IdeMetadata.justifyValues, doc = "主轴对齐：start / center / end / space-between / space-around / space-evenly", defaultValue = "\"start\""),
        PropDef("wrap", PropType.EnumValues, enumValues = IdeMetadata.flexWrapValues, doc = "换行模式：nowrap / wrap / wrap-reverse", defaultValue = "\"nowrap\""),
        // ----- 背景 / 边框 / 圆角 -----
        PropDef("background", PropType.Color, doc = "背景颜色", animatable = true),
        PropDef("borderWidth", PropType.Number, doc = "边框宽度（像素）", animatable = true),
        PropDef("borderColor", PropType.Color, doc = "边框颜色", animatable = true),
        PropDef("cornerRadius", PropType.Number, doc = "圆角半径（像素，单值或四角数组）", animatable = true),
        PropDef("cornerRadiusTL", PropType.Number, doc = "左上圆角", animatable = true),
        PropDef("cornerRadiusTR", PropType.Number, doc = "右上圆角", animatable = true),
        PropDef("cornerRadiusBR", PropType.Number, doc = "右下圆角", animatable = true),
        PropDef("cornerRadiusBL", PropType.Number, doc = "左下圆角", animatable = true),
        // ----- 渐变 -----
        PropDef("gradientType", PropType.EnumValues, enumValues = IdeMetadata.gradientTypeValues, doc = "渐变类型：none / linear / radial"),
        PropDef("gradientAngle", PropType.Number, doc = "线性渐变角度（度，0°=下→上，90°=左→右）", animatable = true),
        PropDef("gradientStops", PropType.Expression, doc = "渐变停止点列表（[[offset, argbColor], ...]，最多 8 段）"),
        PropDef("gradientStartColor", PropType.Color, doc = "两色渐变起点色", animatable = true),
        PropDef("gradientEndColor", PropType.Color, doc = "两色渐变终点色", animatable = true),
        PropDef("radialCenterX", PropType.Number, doc = "径向渐变中心 X（0..1）", animatable = true),
        PropDef("radialCenterY", PropType.Number, doc = "径向渐变中心 Y（0..1）", animatable = true),
        PropDef("radialRadius", PropType.Number, doc = "径向渐变半径（相对短边比例）", animatable = true),
        // ----- 阴影（扁平形式；块形式见 effects {} ）-----
        PropDef("shadowOffsetX", PropType.Number, doc = "阴影 X 偏移", animatable = true),
        PropDef("shadowOffsetY", PropType.Number, doc = "阴影 Y 偏移", animatable = true),
        PropDef("shadowBlur", PropType.Number, doc = "阴影模糊半径", animatable = true),
        PropDef("shadowColor", PropType.Color, doc = "阴影颜色", animatable = true),
        PropDef("shadowSpread", PropType.Number, doc = "阴影扩张", animatable = true),
        PropDef("shadowInset", PropType.Boolean, doc = "是否内阴影（CSS inset 语义）", defaultValue = "false"),
        PropDef("boxShadows", PropType.Expression, doc = "多重阴影列表（[{offsetX,offsetY,blur,spread,color,inset}, ...]）"),
        // ----- 遮罩（扁平形式；块形式见 mask {} ）-----
        PropDef("mask", PropType.EnumValues, enumValues = IdeMetadata.maskTypeValues, doc = "遮罩类型：none / rect / circle / wave / pie / invert_*"),
        PropDef("maskParam1", PropType.Number, doc = "遮罩参数 1（含义随 mask 类型）", animatable = true),
        PropDef("maskParam2", PropType.Number, doc = "遮罩参数 2", animatable = true),
        PropDef("maskParam3", PropType.Number, doc = "遮罩参数 3", animatable = true),
        // ----- 变换 -----
        PropDef("translateX", PropType.Number, doc = "横向平移（像素）", animatable = true),
        PropDef("translateY", PropType.Number, doc = "纵向平移（像素）", animatable = true),
        PropDef("scaleX", PropType.Number, doc = "横向缩放", animatable = true),
        PropDef("scaleY", PropType.Number, doc = "纵向缩放", animatable = true),
        PropDef("rotation", PropType.Number, doc = "2D 旋转角度（度）", animatable = true),
        PropDef("rotateX", PropType.Number, doc = "绕 X 轴旋转（度）", animatable = true),
        PropDef("rotateY", PropType.Number, doc = "绕 Y 轴旋转（度）", animatable = true),
        PropDef("skewX", PropType.Number, doc = "横向倾斜（度）", animatable = true),
        PropDef("skewY", PropType.Number, doc = "纵向倾斜（度）", animatable = true),
        PropDef("perspective", PropType.Number, doc = "透视距离", animatable = true),
        PropDef("transformOrigin", PropType.String, doc = "变换原点（如 \"center\" / \"top left\" / \"25% 75%\" / \"0.5 0.5\"）"),
        // ----- 视觉 / 光标 / 层级 -----
        PropDef("cursor", PropType.EnumValues, enumValues = IdeMetadata.cursorValues, doc = "鼠标光标形状：default / pointer / text / move / resize-* / not-allowed / wait"),
        PropDef("zIndex", PropType.Number, doc = "同级渲染层级偏移"),
        PropDef("dropTarget", PropType.Boolean, doc = "是否为拖放目标", defaultValue = "false"),
        // ----- 绝对定位 -----
        PropDef("x", PropType.Number, doc = "绝对定位 X（layout=absolute 时生效）", animatable = true),
        PropDef("y", PropType.Number, doc = "绝对定位 Y（layout=absolute 时生效）", animatable = true),
        PropDef("layout", PropType.EnumValues, enumValues = IdeMetadata.layoutModeValues, doc = "定位模式：absolute / flex / relative"),
        // ----- 字体（带文字控件通用）-----
        PropDef("font", PropType.String, doc = "namespaced 字体名（三态继承）"),
        PropDef("fontBold", PropType.Boolean, doc = "粗体（null=继承）"),
        PropDef("fontItalic", PropType.Boolean, doc = "斜体"),
        PropDef("fontShadow", PropType.Boolean, doc = "文字阴影"),
        // ----- 其它 -----
        PropDef("tooltip", PropType.String, doc = "悬停提示文本（500ms 后弹出）"),
        PropDef("screenBackground", PropType.Boolean, doc = "是否绘制原版 Screen 背景；仅根元素生效", defaultValue = "true"),
        PropDef("class", PropType.String, doc = "编译期 style-class 类名（空白分隔多类；不进 IR）"),
        PropDef("sticky", PropType.Boolean, doc = "是否吸顶", defaultValue = "false"),
        PropDef("stickyOffset", PropType.Number, doc = "吸顶偏移（像素）"),
        PropDef("tabIndex", PropType.Number, doc = "Tab 遍历顺序"),
        PropDef("hotkey", PropType.String, doc = "快捷键绑定（如 \"Enter\" / \"Ctrl+S\"）"),
    )

    /**
     * 元素级控制属性（visible / opacity / pointerEvents / overflow 等），自动注入给所有 kind。
     *
     * **三处一致铁律**：本组名集合须与 [IdeMetadata.universalElementProps] 及
     * `ComponentExpanderPass.UNIVERSAL_ELEMENT_PROPS` 完全一致——它们同时是组件展开时
     * "调用方写在组件根上、直接透传到展开根元素"的转发白名单。**扩这组会改组件展开语义，
     * 新增通用样式属性请放 [commonStyleProps]，不要放这里。**
     */
    private val universalProps: List<PropDef> = listOf(
        PropDef("visible", PropType.Boolean, doc = "是否可见", defaultValue = "true"),
        PropDef("opacity", PropType.Number, doc = "透明度（0..1）", defaultValue = "1.0", animatable = true),
        PropDef("pointerEvents", PropType.Boolean, doc = "是否拦截指针事件", defaultValue = "true"),
        PropDef(
            "overflow", PropType.EnumValues,
            enumValues = setOf("visible", "hidden", "clip"),
            doc = "溢出处理方式（CSS overflow 心智模型）：visible=不裁剪、hidden/clip=裁剪到自身包围盒",
            defaultValue = "visible",
        ),
        PropDef("aboveItem", PropType.Boolean, doc = "是否渲染在物品图标之上（用于把数量/标签等叠加文本提到物品上层，默认 false）", defaultValue = "false"),
        PropDef("animateLayout", PropType.Boolean, doc = "是否参与布局动画（FLIP）：Taffy 重算导致该元素布局盒位置变化时自动补间位移，默认 false", defaultValue = "false"),
        PropDef("gridColumn", PropType.String, doc = "grid 子项列放置（CSS grid-column 文法，如 \"span 2\" / \"2\" / \"1 / 3\"）"),
        PropDef("gridRow", PropType.String, doc = "grid 子项行放置（CSS grid-row 文法，如 \"span 2\" / \"1 / 3\"）"),
        PropDef("aspectRatio", PropType.Number, doc = "宽高比（宽 / 高）；支持数字（1.5）或比值字符串（\"16/9\"）；未设则不约束"),
    )

    /**
     * 取指定 [kind] 的全部 prop 元数据列表：专有属性 + [commonStyleProps] + [universalProps]，
     * 按 name 去重（前者优先，即 kind 专有 > 通用样式 > 元素控制）。
     *
     * 未识别的 [kind] 返回 [commonStyleProps] + [universalProps]——保持向后兼容，不抛异常。
     */
    fun getProps(kind: String): List<PropDef> =
        (propsByKindCore[kind].orEmpty() + commonStyleProps + universalProps)
            .distinctBy { it.name }

    /** 取指定 [kind] 下名为 [propName] 的元数据；未识别返回 null。 */
    fun getProp(kind: String, propName: String): PropDef? =
        getProps(kind).firstOrNull { it.name == propName }

    /**
     * 取指定 [kind] 下所有 `animatable = true` 的 prop —— 用于 IDE 在
     * `transition / keyframe / frame / pathTransition` 块内补全可补间属性名。
     *
     * 已自动包含 [commonStyleProps] / [universalProps] 中的可补间项。
     */
    fun getAnimatableProps(kind: String): List<PropDef> =
        getProps(kind).filter { it.animatable }

    /**
     * 是否已为 [kind] 登记**专有**元数据。
     *
     * 用于 IDE 端判断"是否对该 kind 启用 PropMetadata 驱动的补全 / 文档"——未登记时退回到
     * 通用元素补全 / 字面量推断属性面板。
     */
    fun hasMetadata(kind: String): Boolean = kind in propsByKindCore

    /**
     * 已登记**专有**元数据的 kind 集合。供 IDE 端 / 测试代码遍历使用。
     */
    val knownKinds: Set<String>
        get() = propsByKindCore.keys
}
